import { defineStore } from "pinia";
import { getToken } from "@/services";
import Institution from "@/models/Institution";
import {
  clearToken,
  loginRequest,
  logoutRequest,
  meRequest,
  persistToken,
} from "@/modules/auth/services/auth-service";
import { canAccessByRules, getRouteAccessRules } from "@/router/route-access";

export const useAuthStore = defineStore("auth", {
  state() {
    return {
      token: getToken(),
      user: null,
      ready: false,
      sessionPromise: null,
    };
  },

  getters: {
    isAuthenticated: (state) => !!state.token,
    permissions: (state) => state.user?.permissions ?? {},
    can: (state) => (permission) => !!state.user?.permissions?.[permission],
    canAccessRoute: (state) => (route) => {
      const rules = getRouteAccessRules(route);

      if (!rules) {
        return true;
      }

      return canAccessByRules(state.user?.permissions, rules);
    },
  },

  actions: {
    hydrateFromStorage() {
      this.token = getToken();
    },

    setUser(user) {
      this.user = user ?? null;
    },

    clearSession() {
      this.token = null;
      this.user = null;
      this.ready = false;
      this.sessionPromise = null;
      clearToken();
      localStorage.removeItem("user");
      localStorage.removeItem("selectedYear");
      Institution.query().deleteAll();
    },

    async login(registration, password) {
      const token = await loginRequest(registration, password);
      persistToken(token);
      this.token = token;
      this.ready = false;
      this.sessionPromise = null;
      await this.ensureSession();
    },

    async fetchMe() {
      const user = await meRequest();
      this.setUser(user);

      return user;
    },

    async ensureSession() {
      this.hydrateFromStorage();

      if (!this.token) {
        this.clearSession();
        return false;
      }

      if (this.ready && this.user) {
        return true;
      }

      if (this.sessionPromise) {
        return this.sessionPromise;
      }

      this.sessionPromise = (async () => {
        try {
          await this.fetchMe();
          await Institution.getInstitution();
          this.ready = true;
          return true;
        } catch (error) {
          this.clearSession();
          return false;
        } finally {
          this.sessionPromise = null;
        }
      })();

      return this.sessionPromise;
    },

    async logout() {
      try {
        if (getToken()) {
          await logoutRequest();
        }
      } finally {
        this.clearSession();
      }
    },
  },
});
